/*
 * No 'use client' here, deliberately.
 *
 * Everything in this file is presentational — it takes props and renders markup, with no state,
 * no effects and no event handlers. That lets a React Server Component render a badge, a rating
 * and a card without shipping their JavaScript to the browser, which matters in a product grid
 * where those repeat two dozen times. The interactive pieces live in their own files and carry
 * the directive themselves.
 */
import type { ReactNode } from 'react';

import { cn } from '../lib/cn.ts';

/* ── Badge ───────────────────────────────────────────────────────────────── */

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'danger' | 'warning';

export interface BadgeProps {
  tone?: BadgeTone;
  children: ReactNode;
  className?: string;
}

export function Badge({ tone = 'neutral', children, className }: BadgeProps) {
  return <span className={cn('awj-badge', `awj-badge--${tone}`, className)}>{children}</span>;
}

/* ── Rating ──────────────────────────────────────────────────────────────── */

export interface RatingProps {
  /** 0–5, may be fractional. */
  value: number;
  /** Shown beside the stars, e.g. "4.3 (12)". */
  label?: string;
  /**
   * What a screen reader hears. The stars themselves are decorative — five repeated glyphs
   * announced individually tell a listener nothing.
   */
  accessibleLabel: string;
  size?: number;
  className?: string;
}

export function Rating({ value, label, accessibleLabel, size = 16, className }: RatingProps) {
  const rounded = Math.round(value);

  return (
    <span className={cn('awj-rating', className)} role="img" aria-label={accessibleLabel}>
      <span className="awj-rating__stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon key={star} size={size} filled={star <= rounded} />
        ))}
      </span>
      {label ? <span className="awj-rating__label">{label}</span> : null}
    </span>
  );
}

function StarIcon({ size, filled }: { size: number; filled: boolean }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="currentColor"
      className={filled ? undefined : 'awj-rating__star--empty'}
      aria-hidden="true"
    >
      <path d="M10 1.6l2.47 5.2 5.53.77-4 4.03.95 5.8L10 14.7 5.05 17.4l.95-5.8-4-4.03 5.53-.77L10 1.6z" />
    </svg>
  );
}

/* ── Skeleton ────────────────────────────────────────────────────────────── */

export interface SkeletonProps {
  variant?: 'block' | 'text' | 'circle';
  width?: string;
  height?: string;
  className?: string;
}

export function Skeleton({ variant = 'block', width, height, className }: SkeletonProps) {
  return (
    <span
      className={cn('awj-skeleton', variant !== 'block' && `awj-skeleton--${variant}`, className)}
      style={{ inlineSize: width, blockSize: height }}
      // Decorative: the loading state itself is announced by whatever owns the region.
      aria-hidden="true"
    />
  );
}

/* ── Empty state ─────────────────────────────────────────────────────────── */

export interface EmptyStateProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div className={cn('awj-empty', className)}>
      {icon ? (
        <span className="awj-empty__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <p className="awj-empty__title">{title}</p>
      {description ? <p className="awj-empty__description">{description}</p> : null}
      {action}
    </div>
  );
}

/* ── Card ────────────────────────────────────────────────────────────────── */

export interface CardProps {
  interactive?: boolean;
  /**
   * Drops the padding, for a card whose child reaches the edge on purpose — a product photo
   * above its details. That child then owns the insets for everything below it.
   */
  flush?: boolean;
  children: ReactNode;
  className?: string;
}

export function Card({ interactive = false, flush = false, children, className }: CardProps) {
  return (
    <div
      className={cn(
        'awj-card',
        interactive && 'awj-card--interactive',
        flush && 'awj-card--flush',
        className,
      )}
    >
      {children}
    </div>
  );
}
